import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import useAuthStore from './authStore';

/**
 * recentsStore — Phase 3.2
 *
 * Tracks the most recently visited entities (students + groups). Surfaces as
 * a "Recents" block in the sidebar footer so admins can jump back without
 * re-searching.
 *
 * Persists to localStorage so it survives reload. Capped at 8 per kind to
 * keep the sidebar from ballooning.
 *
 * Every entry carries the centerId it was visited in. The list is shared by
 * all centers an account can switch between, and without the tag one
 * center's clients showed up in another center's sidebar. Readers must use
 * `forCenter` — untagged legacy entries are hidden.
 */

const MAX_PER_KIND = 8;

function dedupePrepend(list, entry) {
  const filtered = list.filter((e) => e.id !== entry.id);
  return [entry, ...filtered].slice(0, MAX_PER_KIND);
}

const useRecentsStore = create(
  persist(
    (set, get) => ({
      students: [],
      groups: [],

      /**
       * Record a visit. Call from the detail page once on mount.
       * @param {'student'|'group'} kind
       * @param {{ id, label, sublabel? }} entry
       */
      record: (kind, entry) => {
        if (!entry?.id || !entry?.label) return;
        const key = kind === 'student' ? 'students' : 'groups';
        const current = get()[key] || [];
        const centerId = useAuthStore.getState().centerId || null;
        set({ [key]: dedupePrepend(current, { ...entry, centerId, lastSeen: Date.now() }) });
      },

      clear: () => set({ students: [], groups: [] }),
    }),
    {
      name: 'tarteeb-recents',
      version: 1,
    }
  )
);

/** Only the entries recorded in `centerId`. */
export function forCenter(list, centerId) {
  if (!centerId || !Array.isArray(list)) return [];
  return list.filter((e) => e && e.centerId === centerId);
}

export default useRecentsStore;
